import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import {
  FlexBetweenTop,
  FlexCol,
  FlexRowCenter,
} from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { RequestTitle, RequestStatus } from './RequestTitle';
import { RequestDetailItems } from './RequestDetailItems';
import { getRequestDetailInfo } from '../../../utilities/selectors';
import { Button } from '../General/Button';
import { SummaryValue } from '../Cart/Summary/SummaryValue';
import { formatPriceToString, prop } from '../../../utilities';
import { FormTextArea } from '../Form/FormTextArea';
import { FormInput } from '../Form/FormInput';
import { useState } from 'react';

const statusMap = {
  NEW: RequestStatus.WAITING,
  INPROGRESS: RequestStatus.INWORK,
  SHIPPED: RequestStatus.DONE,
};

interface Props {
  removeItemFromOrder: any;
  data: any;
  isB2B: boolean;
  handleCreateOrderClick: any;
  enableCreateOrder: boolean;
  isFetching: boolean;
}

function RequestDetail({
  data,
  isB2B,
  handleCreateOrderClick,
  enableCreateOrder,
  isFetching,
  removeItemFromOrder,
}: Props) {
  const {
    orderId,
    orderStatus,
    ordernr,
    vs,
    orderStatusReadable,
    items,
    totalPrice,
    totalPriceWithoutVat,
    currency,
    vat,
    note,
    deliveryPriceWithoutVat,
    yourNr,
    vatRate,
  } = getRequestDetailInfo(data);

  const [demandNote, setDemandNote] = React.useState<string>(note);
  const [demandNumber, setDemandNumber] = React.useState<string>(yourNr);

  const handleDemandNote = e => {
    setDemandNote(e.target.value);
  };
  const handleDemandNumber = e => {
    setDemandNumber(e.target.value);
  };

  const handleCreateOrderClickWrapper = async () => {
    handleCreateOrderClick({
      note: demandNote && demandNote.length > 0 ? demandNote : null,
      demand_number:
        demandNumber && demandNumber.length > 0 ? demandNumber : null,
    });
  };

  return (
    <Wrapper>
      <RequestTitle
        title={`${__('Dopyt č.')} ${ordernr}`}
        smallText={
          yourNr.length > 0 && yourNr !== '-'
            ? `${__('Vaše číslo')}: ${yourNr}`
            : null
        }
        note={note}
        statusText={orderStatusReadable}
        status={
          statusMap[orderStatus]
            ? statusMap[orderStatus]
            : RequestStatus.WAITING
        }
      />
      <RequestDetailItems
        orderStatus={orderStatus}
        removeItemFromOrder={removeItemFromOrder}
        enableCreateOrder={enableCreateOrder}
        items={items}
        hasMoreInfo={true}
        isB2B={isB2B}
      />
      <ButtomRowWrapper>
        {orderStatus === 'INPROGRESS' && enableCreateOrder ? (
          <FormWrapper>
            <FormInput
              value={demandNumber}
              label={__('Číslo dopytu')}
              onChange={handleDemandNumber}
              notRequired={true}
            />
            <FormTextArea
              value={demandNote}
              label={__('Poznámka')}
              onChange={handleDemandNote}
              defaultValue={prop(demandNote, 'note', '')}
            />
          </FormWrapper>
        ) : (
          <div />
        )}
        <BottomWrapper>
          <SummaryValue
            title={__('Prepravné a balné')}
            value={formatPriceToString(deliveryPriceWithoutVat, currency)}
          />
          <SummaryValue
            title={__('Cena bez DPH')}
            value={formatPriceToString(totalPriceWithoutVat, currency)}
          />
          <SummaryValue
            title={`${__('DPH')} ${vatRate} %`}
            value={formatPriceToString(vat, currency)}
          />
          <SummaryValue
            title={__('Cena s DPH')}
            value={formatPriceToString(totalPrice, currency)}
          />
          {orderStatus === 'INPROGRESS' && enableCreateOrder && (
            <CreateOrderButton
              disabled={isFetching}
              onClick={handleCreateOrderClickWrapper}
            >
              {isFetching ? (
                <FlexRowCenter>
                  <Loader className="marginLeft" small={false} />
                </FlexRowCenter>
              ) : (
                __('Vytvoriť objednávku')
              )}
            </CreateOrderButton>
          )}
        </BottomWrapper>
      </ButtomRowWrapper>
    </Wrapper>
  );
}
const Wrapper = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(1048)};
  margin-right: 0;
  margin-left: auto;

  ${({ theme }) => theme.mediab.l925`
    padding-top: ${rem(32)};
  `}
`;

const ButtomRowWrapper = styled(FlexBetweenTop)`
  margin-top: ${rem(32)};

  ${({ theme }) => theme.mediab.m810`
    width: 100%;
    flex-direction: column;
    align-items: center;
    margin-top: 0;
  `}
`;

const FormWrapper = styled(FlexCol)`
  width: 100%;
  max-width: ${rem(440)};
  margin-right: ${rem(32)};
  ${({ theme }) => theme.mediab.l925`
    margin-right: 0;
    margin-bottom: ${rem(32)}
  `};

  ${({ theme }) => theme.mediab.m810`
    max-width: ${rem(320)};
  `};
`;

const BottomWrapper = styled(FlexCol)`
  ${({ theme }) => theme.mediab.m810`
    margin: 0 auto;
    width: 100%;
  `}
`;

const CreateOrderButton = styled(Button)`
  height: ${rem(48)};
  margin: ${rem(15)} 0 0;
  padding: ${rem(8)};
  width: 100%;
  max-width: ${rem(320)};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  border-radius: ${rem(2)};
  margin: 0 0 ${rem(16)};

  &:disabled {
    background-color: #f9f9f9;
    color: #cccccc;
  }
`;

export const Loader = styled.div<{ className?: string; small?: boolean }>`
  width: ${({ small }) => (small ? rem(20) : rem(40))};
  height: ${({ small }) => (small ? rem(20) : rem(40))};
  border: ${({ small }) =>
    small ? `${rem(4)} solid #cccccc` : `${rem(7)} solid #cccccc`};
  border-top: ${({ small, theme }) =>
    small
      ? `${rem(4)} solid ${theme.blogColors.primary}`
      : `${rem(7)} solid ${theme.blogColors.primary}`};
  margin: 0;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export { RequestDetail };
